import type {
  BookingFormState,
  UpdateGoodsFormModelRecord,
} from "../../../services/update-form/updateFormReducer";
import type { BasicBooking } from "../../graphql/fragments/gql/BasicBooking";
import {
  animalDropdownNoOption,
  animalDropdownYesOption,
} from "../../graphql/useAnimalTypesOptions";
import { customsClassificationDropdownEmptyOption } from "../../graphql/useCustomsClassificationsOptions";
import { loadingPreferenceDropdownNoOption } from "../../graphql/useLoadingPreferenceOptions";
import { includeYesOption } from "../../utils/animalsUtils";
import { formatServerDate } from "../../utils/dates/dateUtils";
import type { Animal } from "../../utils/optionTransformers";

function resolveLivestockType(booking: BasicBooking): Animal | null {
  if (booking.livestock) {
    return includeYesOption(booking.livestockType, booking.livestock)
      ? animalDropdownYesOption.data
      : booking.livestockType;
  }
  return animalDropdownNoOption.data;
}

function resolveGoods(booking: BasicBooking): UpdateGoodsFormModelRecord {
  const goods: UpdateGoodsFormModelRecord = {};

  booking.goods.forEach((g) => {
    goods[g.id] = {
      ...g,
      // Handle case where the underlying booking has a goods type code that is not present in the allowed goods types ("", "invalid_code").
      // This "fake" goods type is used to show the invalid goods type in dropdowns
      goodsType: g.goodsType ?? {
        isValid: false,
        code: g.code,
        id: g.code,
        description: "",
      },
    };
  });

  return goods;
}

export const transformToUpdateBookingFormState = (
  booking: BasicBooking,
  options?: { canBeEmpty?: boolean },
): Required<BookingFormState> => ({
  bookingNo: booking.bookingNo,
  bookingStatusCode: booking.bookingStatusCode,
  goods: resolveGoods(booking),
  customer: {
    ...booking.customer,
  },
  exportReference: booking.exportReference.reference,
  importReference: booking.importReference.reference,
  customerCustomsStatus: booking.customerCustomsStatus,
  customerReference: booking.customerReference,
  customsClassification: booking.customsClassification
    ? {
        ...booking.customsClassification,
      }
    : customsClassificationDropdownEmptyOption.data,
  departureDate: formatServerDate(booking.sailing.departureDate),
  hazardousGoods: booking.hazardousGoods,
  height: booking.height,
  length: booking.length,
  livestock: booking.livestock,
  livestockType: resolveLivestockType(booking),
  loadingListMessage: booking.loadingListMessage,
  loadingPreference: booking.loadingPreference
    ? { ...booking.loadingPreference }
    : loadingPreferenceDropdownNoOption.data,
  noOfAdults: booking.noOfAdults,
  noOfChildren: booking.noOfChildren,
  noOfDrivers: booking.noOfDrivers,
  noOfInfants: booking.noOfInfants,
  noOfPlugins: booking.noOfPlugins,
  pluginTemperature: booking.pluginTemperature,
  route: {
    ...booking.sailing.route,
  },
  receiverCountry: booking.receiverCountry,
  sailing: {
    ...booking.sailing,
    route: {
      ...booking.sailing.route,
    },
  },
  saveAsWaitList: false,
  senderCountry: booking.senderCountry,
  showDaysOnQuay: booking.showDaysOnQuay,
  timestamp: booking.timestamp,
  tradeWeight: booking.tradeWeight,
  trailerRegNo: booking.trailerRegNo,
  vehicleRegNo: booking.vehicleRegNo,
  vehicleType: { ...booking.vehicleType },
  width: booking.width,
  // We currently do not support loaded/empty toggling in the grid, so we only set it for detailed bookings
  empty: options?.canBeEmpty ? resolveEmpty(booking) : null,
});

function resolveEmpty(booking: BasicBooking): boolean {
  return (
    !booking.livestock &&
    !booking.livestockType &&
    booking.goods.length === 0 &&
    !booking.hazardousGoods
  );
}
